import { NodeCell, ElementPropertyConfig, FarrisPropConfigWithData } from "@edp-pmf/grapheditor";
import { MxGraphNS, mxResources } from "@edp-pmf/mxgraph-ts";
import { AutoActivity, Component, Tool, ActualParameter, VariableSetting } from "@gsp-wf/wf-process-model";
import { PropertyHelper } from "./PropertyHelper";
import { BpmnModelHelper, FlowNodeState } from "@edp-pmf/bpmn-model";
import { WfConstants } from "../WfConstants";
import { AutoActivityPropertyKeys } from "./property-keys/AutoActivityPropertyKeys";

/**
 * 服务活动节点
 */
export class AutoActivityCell extends NodeCell {
    clrTypeId: string = AutoActivityCell.CLR_TYPE_ID;
    name: string = "服务";
    nameLanguage: { [lang: string]: string; } = {
      "zh-CHS": "服务",
      "zh-CHT": "服務",
      "en": "Service ",
  };
    static CLR_TYPE_ID: string = AutoActivity.Clr_Type_ID;
    flowElement: AutoActivity;

    imgUrls = {
        [FlowNodeState.Default]: WfConstants.IMAGE_PATH + "/auto.png",
        [FlowNodeState.Selected]: WfConstants.IMAGE_PATH + "/auto_select.svg",
    };

    constructor(value?: any, geometry?: MxGraphNS.mxGeometry, style?: string) {
        super(value, geometry, style);
    }

    createFlowElement(flowElt?: AutoActivity) {
        const flowNode = super.createFlowElement(flowElt) as AutoActivity;
        flowNode.Id = "autoActivity" + BpmnModelHelper.GenerateElementId();

        return flowNode;
    }

    getFarrisPropConfig(): FarrisPropConfigWithData {
        let act = this.flowElement;

        // 组织属性框
        let basicProperty: ElementPropertyConfig = new ElementPropertyConfig();
        let activityProperty: ElementPropertyConfig = new ElementPropertyConfig();
        let propertyData: any = {};

        basicProperty.categoryId = 'basicProperty';
        basicProperty.categoryName = mxResources.get('basicInformation');
        basicProperty.tabId = 'basic';
        basicProperty.tabName = mxResources.get('basicAttributes');
        basicProperty.properties = [
            { propertyID: AutoActivityPropertyKeys.Name, propertyName: mxResources.get('name'), propertyType: 'string', visible: !this.editorUi.multiLangEnabled },
            { propertyID: AutoActivityPropertyKeys.NameLanguage, propertyName: mxResources.get('nameLanguage'), propertyType: 'multiLanguage', visible: this.editorUi.multiLangEnabled },
            { propertyID: AutoActivityPropertyKeys.Id, propertyName: mxResources.get('id'), propertyType: 'string', readonly: true }
        ];


        activityProperty.categoryId = 'activityProperty';
        activityProperty.categoryName = mxResources.get('activityProperty');
        activityProperty.tabId = 'basic';
        activityProperty.tabName = mxResources.get('basicAttributes');
        activityProperty.properties = [
            { propertyID: AutoActivityPropertyKeys.Tools, propertyName: mxResources.get('tools'), propertyType: 'modal' },
            { propertyID: AutoActivityPropertyKeys.variableSettings, propertyName: mxResources.get('variableSettings'), propertyType: 'modal' },
        ];

        propertyData[AutoActivityPropertyKeys.Id] = act.Id;
        propertyData[AutoActivityPropertyKeys.ClrTypeID] = act.ClrTypeID;
        propertyData[AutoActivityPropertyKeys.Name] = act.Name;
        propertyData[AutoActivityPropertyKeys.NameLanguage] = act.NameLanguage || {
            "zh-CHS": act.Name,
        };
        propertyData[AutoActivityPropertyKeys.Tools] = PropertyHelper.getBizComponentList(act.tools as Array<Component>);
        propertyData[AutoActivityPropertyKeys.variableSettings] = PropertyHelper.getVariableSettings(act.Model, act.variableSettings);

        return {
            propertyConfig: [basicProperty, activityProperty],
            propertyData: propertyData
        }
    }

    updateProps(obj: any) {
        const autoActivity = this.flowElement;
        const propertyId: string = obj.propertyID;
        switch (propertyId) {
            case AutoActivityPropertyKeys.Tools:
                this.updateAutoActivityTools(autoActivity, obj);
                break;
            case AutoActivityPropertyKeys.variableSettings:
                this.updateVariableSettings(autoActivity, obj);
                break;
            default:
                super.updateProps(obj);
                break;
        }
    }

    updateAutoActivityTools(autoActivity: AutoActivity, obj: any) {
        let tools = obj.propertyValue as any[];
        autoActivity.tools = new Array<Tool>();
        if (tools && tools.length > 0) {
            for (const tool of tools) {
                let component = new Component(autoActivity.Model);
                component.Id = tool.id;
                component.Name = tool.name;
                // component.Time = tool.executionTime;
                // TODO 写死了构件类型
                component.ComponentType = 'WebServiceComponent';
                component.MetadataId = tool.metadataId;
                component.MethodCode = tool.methodCode;
                component.isForCompensation = tool.isForCompensation;
                component.ActualParameters = [];
                if (tool.actualParameters && (tool.actualParameters as any[]).length > 0) {
                    for (let a of (tool.actualParameters as any[])) {
                        let parameter = new ActualParameter(autoActivity.Model, a.code, a.value);
                        component.ActualParameters.push(parameter);
                    }
                }
                autoActivity.tools.push(component);
            }
        }
    }

    updateVariableSettings(autoActivity: AutoActivity, obj: any) {
        const bpmnModel = autoActivity.Model;
        const extendElements = bpmnModel.DefaultProcess.GetExtensionElements();
        if (autoActivity.variableSettings && autoActivity.variableSettings.length > 0) {
            for (const v of autoActivity.variableSettings) {
                if (v.tag === 'component') {
                    const i = extendElements.findIndex(e => (e instanceof Component && e.Id === v.value));
                    extendElements.splice(i, 1);
                }
            }
        }
        autoActivity.variableSettings = new Array();

        const variables = obj.propertyValue as any[];
        if (variables && variables.length > 0) {
            for (const v of variables) {
                const variableSetting = new VariableSetting(bpmnModel);
                variableSetting.variableCode = v.variableCode;
                variableSetting.variableName = v.variableName;
                variableSetting.tag = v.tag;
                variableSetting.executionTime = v.executionTime;
                variableSetting.value = v.value;
                variableSetting.valueText = v.valueText;

                autoActivity.variableSettings.push(variableSetting);
                if (variableSetting.tag === 'component') {
                    const c = v.component;
                    if (c) {
                        let cpt = new Component(bpmnModel);
                        cpt.MetadataId = c.MetadataId;
                        cpt.MethodCode = c.MethodCode;
                        cpt.Id = c.Id;
                        cpt.Name = c.Name;
                        cpt.ActualParameters = new Array();
                        if (c.ActualParameters && c.ActualParameters.length > 0) {
                            for (let a of c.ActualParameters) {
                                let parameter = new ActualParameter(bpmnModel, a.Name, a.Value);
                                parameter.Tag = a.Tag;
                                cpt.ActualParameters.push(parameter);
                            }
                        }

                        extendElements.push(cpt);
                    }
                }
            }
        }
    }
}
